import React, { useRef, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  useTheme, 
  useMediaQuery, 
  Button, 
  Tooltip, 
  IconButton, 
  Popper,
  Paper,
  ClickAwayListener
} from '@mui/material';
import { ChevronLeft, ChevronRight, Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../../themes/typography';
import { responsiveTypography, responsiveSpacing } from '../../../themes/responsive';
// import { experimentActionColors } from '../../../themes/experimentColors';
import SelectableExperimentCard from '../../../components/shared/SelectableExperimentCard';
import CardCollectionProvider from '../../../components/shared/CardCollectionProvider';
import { createExperimentManagementAdapter } from '../../../utils/experimentDataAdapter';
import { useDiscountManagement } from '../../../hooks/useDiscountManagement';
import CreateExperimentForm from '../../../components/shared/CreateExperimentForm';
import AppliedPricingDialog from '../../../components/shared/AppliedPricingDialog';
import RejectReasonDialog from '../../../components/RejectReasonDialog/RejectReasonDialog';
// Reduced imports - functionality moved to ExperimentCardComplete
import { API_BASE_URL } from '../../../config/api.config';
import { useRejectionModal } from '../../../hooks/useRejectionModal';



const ExperimentColumn = ({ 
  column, 
  onExperimentAction, 
  loading, 
  itemsPerPage = 20, 
  onDataMutation,
  clientId, 
  summarized = false,
  // Props for create experiment form
  currentFilters,
  filterOptions,
  onCreateSuccess,
  onCreateError
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for generic card requirements
  const [experimentStates, setExperimentStates] = React.useState({});
  
  // State for create experiment popover
  const [createExperimentAnchorEl, setCreateExperimentAnchorEl] = React.useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(0);
  
  // Create data adapter
  const dataAdapter = React.useMemo(() => createExperimentManagementAdapter(), []);
  
  // Initialize discount management
  const discountManagement = useDiscountManagement(column.experiments, dataAdapter, clientId, onDataMutation);
  
  // Ref for create experiment form scroll container
  const formScrollRef = useRef(null);

  // Form is in a Popper portal (outside main scroll container).
  // MUI components capture wheel events for scroll-to-change behavior.
  // Solution: Intercept in capture phase, stop propagation, manually scroll page.
  useEffect(() => {
    // Only attach when form is open
    if (!createExperimentAnchorEl) return;

    const handleWheel = (e) => {
      // Find the paper dynamically (ref might not be set when effect runs)
      const paper = document.querySelector('.MuiPaper-elevation8');
      if (!paper || !paper.contains(e.target)) return;

      // CRITICAL: Stop event from reaching MUI components that would block scroll
      e.stopPropagation();
      e.preventDefault();

      // Manually scroll the main page content
      const pageScrollContainer = document.querySelector('.css-4z6nar') || document.documentElement;
      pageScrollContainer.scrollTop += e.deltaY;
    };

    // Capture phase fires BEFORE target/bubble - intercepts before MUI handlers
    document.addEventListener('wheel', handleWheel, { capture: true, passive: false });

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true, passive: false });
    };
  }, [createExperimentAnchorEl]);

  // Initialize rejection modal hook
  const { dialogProps, openRejectDialog } = useRejectionModal(
    async (id, reason) => {
      
      // Handle both single ID and array of IDs
      const ids = Array.isArray(id) ? id : [id];
      
      for (let i = 0; i < ids.length; i++) {
        const experimentId = ids[i];
        const isLastItem = i === ids.length - 1;
        const experiment = column.experiments.find(exp => exp.id === experimentId);
        if (experiment) {
          await onExperimentAction(experiment.id, 'rejected', reason, !isLastItem);
        }
      }
    }
  );
  
  // Initialize experiment states from experiments
  React.useEffect(() => {
    const states = {};
    column.experiments.forEach(experiment => {
      const machineKey = `${experiment.machineId}-${experiment.id}`;
      states[machineKey] = experiment.experiment;
    });
    setExperimentStates(states);
  }, [column.experiments]);

  // Reset pagination when experiments change
  React.useEffect(() => {
    setCurrentPage(0);
  }, [column.experiments]);

  // Pagination calculations
  const totalExperiments = column.experiments.length;
  const totalPages = Math.ceil(totalExperiments / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedExperiments = column.experiments.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString({ es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES', { 
      day: 'numeric', 
      month: 'long' 
    });
  };
  
  // Navigation handler for store ID clicks
  const handleStoreIdClick = (experiment) => {
    if (experiment.group && experiment.location) {
      navigate('/machines', {
        state: {
          defaultTab: 'planograma',
          filters: {
            group: experiment.group,
            location: experiment.location
          }
        }
      });
    }
  };
  
  // Action handlers for generic card
  const handleAccept = async (productKey) => {
    const experiment = column.experiments.find(exp => exp.id === productKey);
    if (experiment) {
      await onExperimentAction(experiment.id, 'accept');
    }
  };
  
  const handleReject = (productKey) => {
    const experiment = column.experiments.find(exp => exp.id === productKey);
    if (experiment) {
      onExperimentAction(experiment.id, 'reject');
    }
  };
  
  const handleBegin = async (productKey) => {
    const experiment = column.experiments.find(exp => exp.id === productKey);
    if (experiment) {
      await onExperimentAction(experiment.id, 'begin');
    }
  };
  
  // Generic status change handler for dropdown
  const handleStatusChange = async (productKey, newStatus) => {
    const experiment = column.experiments.find(exp => exp.id === productKey);
    if (experiment) {
      // If changing to rejected, show dialog first
      if (newStatus === 'rejected') {
        openRejectDialog(productKey);
        return;
      }
      
      await onExperimentAction(experiment.id, newStatus);
    }
  };
  
  
  // Create experiment popover handlers
  const handleCreateExperimentClick = (event) => {
    setCreateExperimentAnchorEl(event.currentTarget);
  };
  
  const handleCreateExperimentClose = () => {
    setCreateExperimentAnchorEl(null);
  };

  React.useEffect(() => {
    if (!createExperimentAnchorEl) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleCreateExperimentClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [createExperimentAnchorEl]);

  // Handle combo selection from menu
  const handleComboSelect = (productKey, candidate) => {
    // Find the experiment using the productKey (experiment ID)
    const experiment = column.experiments.find(exp => exp.id === productKey);
    if (!experiment) return;
    
    // Call discount management to track the change and show save button
    discountManagement.handleComboSelect(productKey, candidate, experiment);
    
    // Update experiment state for accepted experiments (local state only)
    const machineKey = `${experiment.machineId}-${experiment.id}`;
    
    if (experimentStates[machineKey]) {
      setExperimentStates(prev => ({
        ...prev,
        [machineKey]: {
          ...prev[machineKey],
          selectedCombo: candidate
        }
      }));
    }
  };
  
  // Management-specific action buttons
  const renderManagementActions = (enrichedSlot, productKey) => {
    const { experiment } = enrichedSlot;
    
    switch (experiment?.status) {
      case 'pending':
        return (
          <Box sx={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
            <Button
              onClick={() => handleReject(productKey)}
              disabled={false}
              sx={{
                backgroundColor: 'transparent',
                color: colors.text.primary,
                border: `1px solid ${colors.border.medium}`,
                borderRadius: '4px',
                px: '10px',
                py: '2px',
                fontSize: '10px',
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 'auto',
                minHeight: '24px',
                '&:hover': {
                  backgroundColor: colors.background.subtle,
                  borderColor: colors.border.dark,
                  color: colors.text.primary
                },
                '&:disabled': { opacity: 0.6 }
              }}
            >
              {t('reject')}
            </Button>
            <Button
              onClick={() => handleAccept(productKey)}
              disabled={false}
              sx={{
                backgroundColor: colors.background.surface,
                color: colors.text.primary,
                border: `2px solid ${colors.border.dark}`,
                borderRadius: '4px',
                px: '10px',
                py: '2px',
                fontSize: '10px',
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 'auto',
                minHeight: '24px',
                '&:hover': {
                  backgroundColor: colors.background.subtle,
                  borderColor: colors.text.primary,
                  color: colors.text.primary
                },
                '&:disabled': { opacity: 0.6 }
              }}
            >
              {t('accept')}
            </Button>
          </Box>
        );
      
      case 'accepted':
        return (
          <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <Button
              onClick={() => handleReject(productKey)}
              disabled={false}
              sx={{
                backgroundColor: 'transparent',
                color: colors.text.primary,
                border: `1px solid ${colors.border.medium}`,
                borderRadius: '4px',
                px: '10px',
                py: '2px',
                fontSize: '10px',
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 'auto',
                minHeight: '24px',
                '&:hover': {
                  backgroundColor: colors.background.subtle,
                  borderColor: colors.border.dark,
                  color: colors.text.primary
                },
                '&:disabled': { opacity: 0.6 }
              }}
            >
              {t('reject')}
            </Button>
            <Button
              onClick={() => handleBegin(productKey)}
              disabled={false}
              sx={{
                backgroundColor: colors.background.surface,
                color: colors.text.primary,
                border: `2px solid ${colors.border.dark}`,
                borderRadius: '4px',
                px: '10px',
                py: '2px',
                fontSize: '10px',
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 'auto',
                minHeight: '24px',
                '&:hover': {
                  backgroundColor: colors.background.subtle,
                  borderColor: colors.text.primary,
                  color: colors.text.primary
                },
                '&:disabled': { opacity: 0.6 }
              }}
            >
              {t('begin')}
            </Button>
          </Box>
        );
      
      case 'rejected':
        const today = new Date();
        const experimentStart = new Date(experiment.dateFrom);
        const canReaccept = experimentStart > today;
        
        return canReaccept ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={() => handleAccept(productKey)}
              disabled={false}
              sx={{
                backgroundColor: colors.background.surface,
                color: colors.text.primary,
                border: `2px solid ${colors.border.dark}`,
                borderRadius: '4px',
                px: '10px',
                py: '2px',
                fontSize: '10px',
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 'auto',
                minHeight: '24px',
                '&:hover': {
                  backgroundColor: colors.background.subtle,
                  borderColor: colors.text.primary,
                  color: colors.text.primary
                },
                '&:disabled': { opacity: 0.6 }
              }}
            >
              {t('accept')}
            </Button>
          </Box>
        ) : null;
      
      default:
        return null; // running/finished have no actions
    }
  };
  
  return (
    <Box 
      sx={{
        width: isMobile ? '100%' : 'calc(20% - 3.2px)', // Equal width for 5 columns with smaller gap
        flex: isMobile ? 'none' : '1 1 0', // Equal flex grow/shrink
        minWidth: isMobile ? '100%' : '0', // Allow shrinking
        maxWidth: isMobile ? '100%' : 'none', // Remove max width constraint
        backgroundColor: colors.background.surface,
        borderRadius: '12px',
        border: `2px solid ${colors.border.light}`,
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? 'auto' : 'auto', // Let columns grow naturally  
        minHeight: isMobile ? 'auto' : '100vh', // Increased minimum height
        boxShadow: `0 2px 8px ${colors.shadow.soft}`,
        transition: 'all 0.2s ease',
        mb: isMobile ? responsiveSpacing.md : 0,
        '&:hover': {
          borderColor: column.color,
          boxShadow: `0 4px 16px ${colors.shadow.soft}`,
        }
      }}
      data-column-id={column.id} // For future drag-drop implementation
    >
      {/* Precios a reprogramar en el ERP cuando se cambia un experimento ya aceptado */}
      <AppliedPricingDialog
        open={Boolean(discountManagement.priceReview)}
        pricing={discountManagement.priceReview?.pricing}
        onClose={discountManagement.cancelPriceReview}
        onConfirm={discountManagement.confirmPriceReview}
      />

      {/* Column Header */}
      <Box sx={{
        p: responsiveSpacing.md,
        borderBottom: `2px solid ${colors.border.light}`,
        backgroundColor: column.color,
        borderRadius: '10px 10px 0 0',
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-2px',
          left: 0,
          right: 0,
          height: '2px',
          backgroundColor: column.color,
        }
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: responsiveSpacing.xs
          }}>
            <Typography sx={{
              ...responsiveTypography.h4,
              fontWeight: 700,
              color: 'white',
              textShadow: '0 1px 2px rgba(0,0,0,0.2)'
            }}>
              {column.title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Typography sx={{
              ...responsiveTypography.caption,
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              px: '8px',
              py: '2px',
              borderRadius: '12px',
              fontWeight: 700,
              minWidth: '20px',
              textAlign: 'center',
              backdropFilter: 'blur(4px)'
            }}>
              {totalExperiments}
            </Typography>
            {/* Add experiment button - all columns with contextual tooltips */}
            <Tooltip 
              title={
                column.id === 'pending' ? (t('createExperiment') || 'Create New Experiment') :
                column.id === 'accepted' ? (t('createAcceptedExperiment') || 'Create Pre-Accepted Experiment') :
                column.id === 'running' ? (t('createRunningExperiment') || 'Create Running Experiment') :
                column.id === 'finished' ? (t('createFinishedExperiment') || 'Create Finished Experiment') :
                (t('createRejectedExperiment') || 'Create Rejected Experiment')
              } 
              arrow 
              placement="top"
            >
              <IconButton
                size="small"
                onClick={handleCreateExperimentClick}
                sx={{
                  width: '28px',
                  height: '28px',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: '1.5px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  // Subtle glow effect for discoverability
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: '8px',
                    background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                    zIndex: -1,
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.35)',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    transform: 'scale(1.1)',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15), 0 0 0 2px rgba(255, 255, 255, 0.2)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                  }
                }}
              >
                <Add sx={{ fontSize: '18px', fontWeight: 'bold' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Column Content */}
      <Box 
        sx={{
          flex: 1,
          p: responsiveSpacing.md,
          display: 'flex',
          flexDirection: 'column',
          gap: responsiveSpacing.md,
          overflowY: 'visible', // Restore original overflow
          minHeight: 'auto', // Restore original min height
          // Custom scrollbar for individual columns (when needed)
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: colors.background.subtle,
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.border.medium,
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: column.color,
            },
          },
        }}
        data-column-content={column.id} // For future drag-drop implementation
      >
        {column.experiments.length === 0 ? (
          <Typography sx={{ 
            ...responsiveTypography.body,
            fontWeight: 500,
            color: colors.text.secondary,
            textAlign: 'center',
            py: responsiveSpacing.md
          }}>
            {t('noExperimentsFound')}
          </Typography>
        ) : (() => {
          // Create product data map for all experiments (needed for combo selection)
          const productDataMap = {};
          paginatedExperiments.forEach(experiment => {
            const productKey = experiment.id;
            productDataMap[productKey] = {
              avgSales: experiment.experiment.previousUnitsSold,
              kpiString: `€${experiment.productMargin} ${t('margin')} (${t('vsMachine')} €${experiment.machineAvgMargin})\n${experiment.productAvgSales} ${t('averageUnitsSold')} (${t('vsMachine')} ${experiment.machineAvgSales})`,
              candidates: experiment.experiment.comboPrices.map(combo => ({
                name: combo.candidateName,
                price: combo.totalWithDiscount.toFixed(2)
              })),
              startDate: formatDate(experiment.experiment.dateFrom),
              endDate: formatDate(experiment.experiment.dateTo)
            };
          });

          // Use new architecture for ALL columns
          return (
            <CardCollectionProvider
              onStatusChange={async (experimentIds, newStatus, skipRefresh = false) => {
                // Handle status change for the new architecture
                const ids = Array.isArray(experimentIds) ? experimentIds : [experimentIds];
                
                // Special handling for rejection
                if (newStatus === 'rejected') {
                  openRejectDialog(ids.length === 1 ? ids[0] : ids);
                  return { success: false, reason: 'dialog' };
                }
                
                // Use the existing onExperimentAction
                const results = [];
                for (let i = 0; i < ids.length; i += 1) {
                  const experimentId = ids[i];
                  try {
                    // Los ids restantes viajan como cola: si este experimento abre el diálogo de
                    // precios, el confirm del diálogo los retoma. Antes el bucle se cortaba aquí
                    // y el resto de la selección se quedaba sin aplicar en silencio.
                    const result = await onExperimentAction(experimentId, newStatus, '', skipRefresh, null, ids.slice(i + 1));
                    // Se abrió el diálogo de precios: nada se ha movido aún, el guardado ocurre
                    // al confirmar (y la cola sigue desde allí).
                    if (result?.reason === 'dialog') {
                      return { success: false, reason: 'dialog' };
                    }
                    results.push({ id: experimentId, success: true, result });
                  } catch (error) {
                    results.push({ id: experimentId, success: false, error });
                  }
                }
                
                return { 
                  success: results.every(r => r.success), 
                  results 
                };
              }}
              onDataRefresh={onDataMutation}
              onOperationSuccess={onCreateSuccess}
              onOperationError={onCreateError}
              clientId={clientId}
            >
              {paginatedExperiments.map((experiment) => {
                const productKey = experiment.id;
                const machineKey = `${experiment.machineId}-${experiment.id}`;
                
                const titleComponent = (
                  <Typography sx={{
                    fontWeight: 600,
                    fontSize: '13px',
                    color: colors.text.primary,
                    lineHeight: 1.2
                  }}>
                    {summarized ? 
                      experiment.originalProduct?.name : 
                      experiment.productName
                    }
                  </Typography>
                );
                
                return (
                  <SelectableExperimentCard
                    key={experiment.id}
                    item={experiment}
                    productKey={productKey}
                    machineKey={machineKey}
                    productData={productDataMap}
                    clientId={clientId}
                    
                    // Display props
                    showStoreId={true}
                    showBadge={true}
                    handleStoreIdClick={handleStoreIdClick}
                    
                    // Customization
                    titleComponent={titleComponent}
                    summarized={summarized}
                    focusedItemId={null}
                    
                    // Event handlers
                    onAccept={handleAccept}
                    onReject={handleReject}
                    onStartEarly={handleBegin}
                    onComboSelect={handleComboSelect}
                    onStatusChange={handleStatusChange}
                    
                    // State
                    experimentStates={experimentStates}
                    onExperimentStateChange={setExperimentStates}
                    
                    // Shared functionality
                    discountManagement={discountManagement}
                    renderActionButtons={renderManagementActions}
                    dataAdapter={dataAdapter}
                    
                    // Utils
                    t={t}
                  />
                );
              })}
            </CardCollectionProvider>
          );
        })()}
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            p: responsiveSpacing.sm,
            borderTop: `1px solid ${colors.border.light}`,
            backgroundColor: 'rgba(255, 255, 255, 0.5)'
          }}>
            <IconButton
              onClick={handlePreviousPage}
              disabled={currentPage === 0}
              size="small"
              sx={{
                backgroundColor: currentPage === 0 ? 'transparent' : colors.background.surface,
                color: currentPage === 0 ? colors.text.secondary : colors.text.primary,
                '&:hover': {
                  backgroundColor: currentPage === 0 ? 'transparent' : colors.background.subtle,
                },
                '&:disabled': {
                  opacity: 0.4
                }
              }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            
            <Typography sx={{
              ...responsiveTypography.small,
              fontWeight: 600,
              color: colors.text.primary,
              minWidth: '60px',
              textAlign: 'center'
            }}>
              {startIndex + 1}-{Math.min(endIndex, totalExperiments)} of {totalExperiments}
            </Typography>
            
            <IconButton
              onClick={handleNextPage}
              disabled={currentPage === totalPages - 1}
              size="small"
              sx={{
                backgroundColor: currentPage === totalPages - 1 ? 'transparent' : colors.background.surface,
                color: currentPage === totalPages - 1 ? colors.text.secondary : colors.text.primary,
                '&:hover': {
                  backgroundColor: currentPage === totalPages - 1 ? 'transparent' : colors.background.subtle,
                },
                '&:disabled': {
                  opacity: 0.4
                }
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
      
      
      {/* Create Experiment Popover */}
      <Popper
        open={Boolean(createExperimentAnchorEl)}
        anchorEl={createExperimentAnchorEl}
        placement="bottom"
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        style={{ zIndex: Math.max(1, (theme?.zIndex?.modal || 1300) - 1) }}
      >
        <ClickAwayListener
          onClickAway={(event) => {
            const targetNode = event.target instanceof Node ? event.target : null;

            if (targetNode && createExperimentAnchorEl?.contains(targetNode)) {
              return;
            }

            handleCreateExperimentClose();
          }}
        >
          <Paper
            ref={formScrollRef}
            elevation={8}
            sx={{
              borderRadius: '12px',
              border: `1px solid ${colors.border.light}`,
              boxShadow: `0 8px 32px ${colors.shadow.soft}`,
              backdropFilter: 'blur(16px)',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              mt: '8px'
            }}
          >
            <CreateExperimentForm
              open={Boolean(createExperimentAnchorEl)}
              column={column}
              currentFilters={currentFilters}
              filterOptions={filterOptions}
              clientId={clientId}
              onClose={handleCreateExperimentClose}
              onSuccess={() => {
                // CreateExperimentForm entrega response.data (objeto); el toast espera un string —
                // renderizar el objeto crasheaba React ("Objects are not valid as a React child").
                onCreateSuccess?.(t('experimentCreated') || 'Experimento creado exitosamente');
                // Refresh experiment data using custom event
                window.dispatchEvent(new CustomEvent('experimentMutation', { 
                  detail: { 
                    action: 'create',
                    clientId: clientId
                  }
                }));
              }}
              onError={onCreateError}
              t={t}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
      
      {/* Reject Reason Dialog */}
      <RejectReasonDialog {...dialogProps} />
    </Box>
  );
};

export default ExperimentColumn;
